import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { usePushRules, PushRuleState } from '../../../../../hooks/usePushRules';
import { useI18n } from '../../../../../context/I18nContext';
import './NotificationsPage.css';

const NotificationsPage = ({ roomId }) => {
  const { t } = useI18n();
  const { getRoomState, setRoomState } = usePushRules();

  const [selectedState, setSelectedState] = useState(PushRuleState.NOTIFY);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load initial state when roomId changes
  useEffect(() => {
    if (roomId) {
      let currentState = getRoomState(roomId);
      // MENTIONS_ONLY removed from UI; treat legacy state as muted
      if (currentState === PushRuleState.MENTIONS_ONLY) {
        currentState = PushRuleState.DONT_NOTIFY;
      }
      setSelectedState(currentState);
    }
  }, [roomId, getRoomState]);

  // Handle notification state change
  const handleChange = async (newState) => {
    if (newState === selectedState || isLoading) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await setRoomState(roomId, newState);
      setSelectedState(newState);
    } catch {
      setError(t('RoomSettings.notificationsError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="room-settings-page notifications-page">
      <div className="room-settings-section">
        <div className="room-settings-section-header">
          <h3 className="room-settings-section-title">{t('RoomSettings.notificationsTitle')}</h3>
          <p className="room-settings-description">
            {t('RoomSettings.notificationsDescription')}
          </p>
          <div className="notifications-per-user-note">
            <ion-icon name="person-outline" />
            <span>
              {t('RoomSettings.notificationsPerUserNote')}
            </span>
          </div>
          <div className="room-settings-section-divider" />
        </div>

        <div className="room-settings-option-group">
          {/* All messages option */}
          <label className={`room-settings-option ${isLoading ? 'disabled' : ''}`}>
            <input
              id="room-settings-notify-all"
              type="radio"
              name="notificationState"
              value={PushRuleState.NOTIFY}
              checked={selectedState === PushRuleState.NOTIFY}
              onChange={() => handleChange(PushRuleState.NOTIFY)}
              disabled={isLoading}
            />
            <div className="room-settings-option-content">
              <span className="room-settings-option-label">
                {t('RoomSettings.notifyAllMessages')}
              </span>
              <span className="room-settings-option-description">
                {t('RoomSettings.notifyAllMessagesDesc')}
              </span>
            </div>
          </label>

          {/* Mute option */}
          <label className={`room-settings-option ${isLoading ? 'disabled' : ''}`}>
            <input
              id="room-settings-notify-mute"
              type="radio"
              name="notificationState"
              value={PushRuleState.DONT_NOTIFY}
              checked={selectedState === PushRuleState.DONT_NOTIFY}
              onChange={() => handleChange(PushRuleState.DONT_NOTIFY)}
              disabled={isLoading}
            />
            <div className="room-settings-option-content">
              <span className="room-settings-option-label">
                {t('RoomSettings.notifyMute')}
              </span>
              <span className="room-settings-option-description">
                {t('RoomSettings.notifyMuteDesc')}
              </span>
            </div>
          </label>
        </div>

        {/* Loading indicator */}
        {isLoading && (
          <div className="notifications-loading">
            <span>{t('RoomSettings.notificationsSaving')}</span>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="notifications-error">
            <ion-icon name="alert-circle-outline" />
            <span>{error}</span>
          </div>
        )}
      </div>
    </div>
  );
};

NotificationsPage.propTypes = {
  roomId: PropTypes.string.isRequired,
};

export default NotificationsPage;
